import { Selection } from "@tiptap/pm/state";

import { appendBlock } from "@/components/doc/right-sidebar/create-blocks/utils/appendBlock";

import type { SlashCommandType } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { Editor } from "@tiptap/react";

/**
 * While the editor stays focused, ProseMirror only does an incremental DOM sync,
 * which leaves the native caret outside a freshly mounted table-cell node view.
 * A blur -> refocus transition (after the cell DOM mounts) forces a full
 * selectionToDOM, the same way the right-sidebar button gets it for free.
 */
function refocusCellCaret(tiptap: Editor) {
  const targetPos = tiptap.state.selection.from;
  tiptap.view.dom.blur();
  requestAnimationFrame(() => {
    if (tiptap.isDestroyed) return;
    tiptap.view.focus();
    tiptap.view.dispatch(
      tiptap.state.tr.setSelection(Selection.near(tiptap.state.doc.resolve(targetPos), 1)),
    );
  });
}

export function executeSlashCommand(
  doc: DocumentModel,
  slashPos: number,
  endPos: number,
  kind: SlashCommandType,
): boolean {
  const { tiptap } = doc.editor;
  if (!tiptap) return false;

  const chain = tiptap.chain().deleteRange({ from: slashPos, to: endPos });

  switch (kind) {
    case "text":
      return chain.setNode("paragraph").run();
    case "heading1":
      return chain.toggleHeading({ level: 1 }).run();
    case "heading2":
      return chain.toggleHeading({ level: 2 }).run();
    case "heading3":
      return chain.toggleHeading({ level: 3 }).run();
    case "bulletList":
      return chain.toggleBulletList().run();
    case "orderedList":
      return chain.toggleOrderedList().run();
    case "taskList":
      return chain.toggleTaskList().run();
    case "blockquote":
      return chain.unsetCallout().toggleBlockquote().run();
    case "callout":
      return chain.unsetBlockquote().toggleCallout().run();
    case "code":
      return chain.setCodeBlock().run();
    case "horizontalRule":
    case "table": {
      // Reuse the right-sidebar "Add block" behavior for cursor placement.
      const deleted = chain.run();
      appendBlock(doc, kind);
      if (kind === "table") refocusCellCaret(tiptap);
      return deleted;
    }
    case "alignLeft":
      return chain.setAlign("left").run();
    case "alignCenter":
      return chain.setAlign("center").run();
    case "alignRight":
      return chain.setAlign("right").run();
    default: {
      kind satisfies never;
      return false;
    }
  }
}
